import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AiOutlineClose } from "react-icons/ai";
import api from "../api";
import "./styles/HelpModal.css";

export default function HelpModal({ onClose }) {
  const [form, setForm] = useState({
    type: "bug",
    subject: "",
    description: "",
    email: "",
  });

  const [isSending, setIsSending] = useState(false);
  const [ticketId, setTicketId] = useState(null); // ⇢ null = form view

  /* ---------------- form helpers ---------------- */
  const handle = (field) => (e) =>
    setForm({ ...form, [field]: e.target.value });

  /* ---------------- submit ---------------------- */
  const submit = async (e) => {
    e.preventDefault();
    if (isSending) return;
    setIsSending(true);

    const payload = {
      issue_type: form.type,
      subject: form.subject,
      description: form.description,
      email: form.email,
      user_id: localStorage.getItem("uid") || undefined,
    };

    try {
      const { data } = await api.post("/support_ticket/", payload);

      if (data.status === "success") {
        setTicketId(data.ticket_id); // ⇢ switch to receipt view
      } else {
        throw new Error(data.message || "Server error");
      }
    } catch (err) {
      console.error(err);
      setTicketId("error"); // show fallback error view
    } finally {
      setIsSending(false);
    }
  };

  /* ------------- modal content ------------------ */
  const renderBody = () => {
    /* (A) – original form ------------------------------------------ */
    if (!ticketId) {
      return (
        <form onSubmit={submit} className="fade-in">
          <label>
            Issue type
            <br />
            <select value={form.type} onChange={handle("type")}>
              <option value="bug">Bug</option>
              <option value="billing">Billing</option>
              <option value="feature">Feature request</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label>
            Subject
            <br />
            <input required value={form.subject} onChange={handle("subject")} />
          </label>

          <label>
            Description
            <br />
            <textarea
              required
              rows="4"
              value={form.description}
              onChange={handle("description")}
            />
          </label>

          <label>
            Email
            <br />
            <input
              type="email"
              required
              value={form.email}
              onChange={handle("email")}
            />
          </label>

          <div className="actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSending}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSending}
            >
              {isSending ? "Sending…" : "Send"}
            </button>
          </div>
        </form>
      );
    }

    /* (B) – success receipt ---------------------------------------- */
    if (ticketId !== "error") {
      return (
        <div className="receipt fade-in">
          {/* heading row */}
          <div className="receipt-head">
            <h2>Ticket received</h2>
            <span className="tick">&#x2705;</span>
          </div>

          <p className="muted">
            Thanks for letting us know!
            <br />
            Your reference&nbsp;ID:
            <br />
            <strong className="code">#{ticketId}</strong>
          </p>

          <p className="muted">
            We aim to respond within{" "}
            <strong>24-72&nbsp;hours&nbsp;(Mon-Fri)</strong>. Updates will go to{" "}
            <strong>{form.email}</strong>.
          </p>

          <button className="btn btn-primary wide" onClick={onClose}>
            Close
          </button>
        </div>
      );
    }

    /* (C) – fallback error ----------------------------------------- */
    return (
      <div className="receipt fade-in">
        <h2>Uh-oh 😕</h2>
        <p>
          Something went wrong while sending your ticket. Please try again in a
          moment or e-mail us directly at{" "}
          <a href="mailto:support@bestday2marry.com">
            support@bestday2marry.com
          </a>
          .
        </p>
        <div className="actions">
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  };

  /* ------------- render overlay & modal --------------------------- */
  return createPortal(
    <div className="help-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <AiOutlineClose size={20} />
        </button>

        {!ticketId && <h2>Submit a Support Ticket</h2>}

        {renderBody()}
      </div>
    </div>,
    document.body
  );
}
